import { LoadingOverlayComponent } from '@/app/atoms/loading-overlay/loading-overlay.component';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { AgencyDto } from '@/app/common/general-types/agency.dto';
import { BusinessAccount } from '@/app/common/general-types/business-account.dto';
import { ImageTypesEnum, S3BucketEnum } from '@/app/common/general-types/media-assets.dto';
import { AgencyService } from '@/app/services/agency/agency.service';
import { BusinessAccountService } from '@/app/services/business-account/business-account.service';
import { ImageService } from '@/app/services/image/image.service';
import { S3uploaderService } from '@/app/services/s3uploader/s3uploader.service';
import {
  ChangeDetectorRef,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  effect,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormField, form, minLength, required } from '@angular/forms/signals';
import { Router } from '@angular/router';
import { catchError, firstValueFrom, map, of, Subject, switchMap, take, tap } from 'rxjs';
import { BrandInsightsState } from '../../brand-insights.state';

type AgencyLoadState = 'idle' | 'loading' | 'loaded' | 'error';
type CurrencyCodeOption = 'usd' | 'gbp' | 'eur';

type FlagSelectOption<T extends string> = {
  value: T;
  label: string;
  flagUrl: string;
};

const FLAG_BASE = 'https://shout-images-static.s3.eu-west-1.amazonaws.com';

const CURRENCY_OPTIONS: readonly FlagSelectOption<CurrencyCodeOption>[] = [
  { value: 'usd', label: 'USD', flagUrl: `${FLAG_BASE}/flag-usa.png` },
  { value: 'gbp', label: 'GBP', flagUrl: `${FLAG_BASE}/flag-uk.png` },
  { value: 'eur', label: 'EUR', flagUrl: `${FLAG_BASE}/flag-ireland.png` },
];

function normalizeCurrencyCode(value: string | null | undefined): CurrencyCodeOption {
  const code = (value ?? '').trim().toLowerCase();
  if (code === 'usd' || code === 'eur') {
    return code;
  }
  return 'gbp';
}

function parseAutoDeclineDays(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  const days = Number(trimmed);
  if (!Number.isFinite(days) || days <= 0) {
    return null;
  }
  return Math.min(90, Math.max(1, Math.floor(days)));
}

type AgencyLoadResult = {
  status: AgencyLoadState;
  agency: AgencyDto | undefined;
};

@Component({
  selector: 'brand-ins-profile',
  imports: [FormField, LoadingOverlayComponent],
  templateUrl: './profile.html',
  styleUrl: './profile.css',
  host: { class: 'block h-full min-h-0 w-full min-w-0' },
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class Profile implements OnChanges {
  @Input() brand: BusinessAccount | undefined;
  @Output() brandUpdated = new EventEmitter<BusinessAccount>();

  readonly currencyOptions = CURRENCY_OPTIONS;

  businessInformationModel = signal({
    companyName: '',
    website: '',
    instagram: '',
    tiktok: '',
    about: '',
    coverPhoto: '',
    companyLogo: '',
    currencyCode: 'gbp' as CurrencyCodeOption,
  });

  readonly selectedCurrencyOption = computed(() => {
    const code = normalizeCurrencyCode(this.businessInformationModel().currencyCode);
    return this.currencyOptions.find((option) => option.value === code) ?? null;
  });

  businessInformationForm = form(this.businessInformationModel, (schemaPath) => {
    required(schemaPath.companyName, { message: 'Company name is required' });
    minLength(schemaPath.companyName, 2, { message: 'Company name must be at least 2 characters long' });
    required(schemaPath.website, { message: 'Website is required' });
  });

  businessInformationUpdated = signal<boolean>(false);
  private isInitialized = false;

  selectedFiles: any[] = [];
  companyLogo = signal<string>('');
  imageLoading = false;

  readonly S3Bucket = S3BucketEnum;
  readonly ImageTypes = ImageTypesEnum;

  updatingBusinessInformation = signal<boolean>(false);
  autoDeclineDaysInput = signal('');
  savingAutoDeclineDays = signal(false);
  autoDeclineDaysError = signal<string | null>(null);

  associatedAgency = signal<AgencyDto | undefined>(undefined);
  agencyLoadingState = signal<AgencyLoadState>('idle');

  private businessAccountService = inject(BusinessAccountService);
  private toastNotificationService = inject(ToastNotificationService);
  private uploadService = inject(S3uploaderService);
  readonly imageService = inject(ImageService);
  private readonly agencyService = inject(AgencyService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly brandState = inject(BrandInsightsState);

  private readonly loadAgencyId$ = new Subject<string | null>();

  constructor() {
    effect(() => {
      this.businessInformationModel();
      if (this.isInitialized) {
        this.businessInformationUpdated.set(true);
      }
    });

    effect(() => {
      const fromState = this.brandState.brand();
      if (fromState && fromState.id !== this.brand?.id) {
        this.brand = fromState;
        this.applyBrand(fromState);
      }
    });

    this.loadAgencyId$
      .pipe(
        tap((agencyId) => {
          this.associatedAgency.set(undefined);
          this.agencyLoadingState.set(agencyId ? 'loading' : 'idle');
          this.refreshView();
        }),
        switchMap((agencyId) => {
          if (!agencyId) {
            return of<AgencyLoadResult>({ status: 'idle', agency: undefined });
          }
          return this.agencyService.getAgencyById(agencyId).pipe(
            map((agency): AgencyLoadResult => ({ status: 'loaded', agency })),
            catchError((err) => {
              console.error('Error fetching associated agency:', err);
              return of<AgencyLoadResult>({ status: 'error', agency: undefined });
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this.associatedAgency.set(result.agency);
        this.agencyLoadingState.set(result.status);
        this.refreshView();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['brand']) {
      return;
    }
    this.applyBrand(this.brand);
  }

  private applyBrand(b: BusinessAccount | undefined): void {
    if (!b) {
      this.loadAgencyId$.next(null);
      return;
    }
    this.isInitialized = false;
    this.businessInformationUpdated.set(false);
    this.businessInformationModel.set({
      companyName: b.companyName ?? '',
      website: b.website ?? '',
      instagram: b.instagram ?? '',
      tiktok: b.tiktok ?? '',
      about: b.about ?? '',
      coverPhoto: (b.coverPhoto as string | null) ?? '',
      companyLogo: b.companyLogo ?? '',
      currencyCode: normalizeCurrencyCode(b.currencyCode),
    });
    this.companyLogo.set(b.companyLogo ?? '');
    this.autoDeclineDaysInput.set(
      b.autoDeclineDays != null && b.autoDeclineDays > 0 ? String(b.autoDeclineDays) : '',
    );
    this.autoDeclineDaysError.set(null);
    this.isInitialized = true;
    this.loadAgencyId$.next(this.agencyIdFromBrand(b));
  }

  hasAssociatedAgencyId(): boolean {
    return !!this.brand?.agencyId?.trim();
  }

  viewAssociatedAgency(event?: Event): void {
    event?.preventDefault();
    const agencyId = this.associatedAgency()?.id;
    if (!agencyId) {
      return;
    }
    void this.router.navigate(['/insights/agency', agencyId]);
  }

  selectFile(event: any): void {
    this.selectedFiles = event.target.files;
    void this.upload(this.selectedFiles);
  }

  async upload(selectedFiles: any) {
    const brand = this.brand;
    if (!brand?.id) {
      return;
    }

    this.imageLoading = true;
    const uploadedFile: { file: string; fileKey: string } | null = await this.uploadService.uploadFile(
      selectedFiles,
      this.S3Bucket.PROFILE_PHOTOS,
      this.ImageTypes.GALLERY
    );

    if (uploadedFile) {
      this.companyLogo.set(uploadedFile.fileKey);
      try {
        const user_updated = (await firstValueFrom(
          this.businessAccountService.updateBusinessAccountPartial(brand.id, { companyLogo: this.companyLogo() })
        )) as BusinessAccount;
        this.companyLogo.set(user_updated.companyLogo);
        this.brandUpdated.emit(user_updated);
        this.brandState.patchBrand(user_updated);
      } catch (error) {
        console.error('Error updating user:', error);
      }
    } else {
      console.error('No uploaded file');
    }
    this.imageLoading = false;
  }

  saveChanges() {
    const brand = this.brand;
    if (!brand?.id) {
      return;
    }

    const businessInformationTouched =
      this.businessInformationForm.companyName().touched() ||
      this.businessInformationForm.website().touched() ||
      this.businessInformationForm.instagram().touched() ||
      this.businessInformationForm.tiktok().touched() ||
      this.businessInformationForm.about().touched();
    const currencyChanged =
      normalizeCurrencyCode(brand.currencyCode) !== this.businessInformationModel().currencyCode;

    if (this.businessInformationUpdated() && (businessInformationTouched || currencyChanged)) {
      this.updatingBusinessInformation.set(true);
      this.businessAccountService
        .updateBusinessAccountPartial(brand.id, {
          companyName: this.businessInformationModel().companyName,
          website: this.businessInformationModel().website,
          instagram: this.businessInformationModel().instagram,
          tiktok: this.businessInformationModel().tiktok,
          about: this.businessInformationModel().about,
          currencyCode: this.businessInformationModel().currencyCode,
        })
        .pipe(take(1))
        .subscribe((businessAccount) => {
          this.brandUpdated.emit(businessAccount as BusinessAccount);
          this.brandState.patchBrand(businessAccount as BusinessAccount);
          setTimeout(() => {
            this.updatingBusinessInformation.set(false);
          }, 500);
        });
    }
  }

  onAutoDeclineDaysInput(event: Event): void {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) {
      return;
    }
    this.autoDeclineDaysInput.set(target.value);
    this.autoDeclineDaysError.set(null);
  }

  canResetAutoDeclineDays(): boolean {
    const saved = this.brand?.autoDeclineDays;
    if (typeof saved === 'number' && saved > 0) {
      return true;
    }
    return this.autoDeclineDaysInput().trim() !== '';
  }

  resetAutoDeclineDays(): void {
    this.autoDeclineDaysInput.set('');
    this.autoDeclineDaysError.set(null);
    this.saveAutoDeclineDays();
  }

  saveAutoDeclineDays(): void {
    const brand = this.brand;
    if (!brand?.id) {
      return;
    }

    const raw = this.autoDeclineDaysInput().trim();
    const parsed = parseAutoDeclineDays(raw);
    if (raw !== '' && raw !== '0' && parsed == null) {
      this.autoDeclineDaysError.set('Enter a whole number between 1 and 90, or leave blank to disable.');
      void this.toastNotificationService.alert({
        title: 'Review window not saved',
        message: 'Enter a whole number between 1 and 90, or leave blank to disable.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    this.savingAutoDeclineDays.set(true);
    this.businessAccountService
      .updateBusinessAccountPartial(brand.id, { autoDeclineDays: parsed })
      .pipe(take(1))
      .subscribe({
        next: (businessAccount) => {
          const updated = businessAccount as BusinessAccount;
          this.brandUpdated.emit(updated);
          this.brandState.patchBrand(updated);
          this.autoDeclineDaysInput.set(
            updated.autoDeclineDays != null && updated.autoDeclineDays > 0
              ? String(updated.autoDeclineDays)
              : '',
          );
          this.savingAutoDeclineDays.set(false);
          const days = updated.autoDeclineDays;
          void this.toastNotificationService.alert({
            title: 'Review window saved',
            message:
              days != null && days > 0
                ? `Applied creators will be auto-declined after ${days} days.`
                : 'Auto-decline is now disabled for new applications.',
            type: 'success',
            icon: 'success',
          });
          this.refreshView();
        },
        error: () => {
          this.autoDeclineDaysError.set('Could not save the review window.');
          this.savingAutoDeclineDays.set(false);
          void this.toastNotificationService.alert({
            title: 'Review window not saved',
            message: 'Could not save the review window. Try again.',
            type: 'error',
            icon: 'error',
          });
          this.refreshView();
        },
      });
  }

  onCurrencySelected(value: CurrencyCodeOption): void {
    this.businessInformationModel.update((model) => ({
      ...model,
      currencyCode: value,
    }));
    if (this.isInitialized) {
      this.businessInformationUpdated.set(true);
    }
  }

  private agencyIdFromBrand(brand: BusinessAccount): string | null {
    const agencyId = brand.agencyId?.trim();
    return agencyId ? agencyId : null;
  }

  private refreshView(): void {
    this.cdr.detectChanges();
  }
}
